import { isNil } from 'lodash'
import {
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoConeCourse,
  GeckoLegacyBoilerRepairRegion,
  GeckoLegacyBoilerWall,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPlate,
  GeckoPolygonalPlate,
  GeckoShellCourse,
} from 'types'

import {
  getMostRecentPlateInBin,
  getMostRecentPolygonalPlateInBin,
} from './repairHelpers'
export type GetManufactureSpecsForBinFn = (props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  assetModel: GeckoAssetModel
  forecastComponentConfig: ForecastComponentConfig | undefined
  latestDateToUse: Date | undefined
}) => {
  nominalThickness: GeckoMeasurement | undefined
  installationDate: Date | undefined
}
/**
 * A function to get the manufacture specs (nominal thickness and/or installation date) for a given bin.
 *
 * Note: If you use forecastComponentConfig values in your component's getManufactureSpecsFor<ComponentType> function,
 *       please pull the relevant data from it in this function and pass it in as a prop so that it will be easier to
 *       migrate the values to the RAM.
 *
 * @param props.binBoundsToGetSpecsFor    The bounds of the bin to get the specs for
 * @param props.assetModel                The asset model to get the specs from
 * @param props.targetComponentId         The ID of the component to get the specs for
 * @param props.forecastComponentConfig   The forecast component config for the component
 */
export const getManufactureSpecsForBin: GetManufactureSpecsForBinFn = (
  props
) => {
  const {
    binBoundsToGetSpecsFor,
    assetModel,
    forecastComponentConfig,
    latestDateToUse,
  } = props
  const targetComponentId = binBoundsToGetSpecsFor.componentID
  const component = assetModel.data.components.get(targetComponentId)
  if (!component) {
    throw new Error(
      `getManufactureSpecsForBinFn was passed a targetComponentId (${targetComponentId}) that's not in the provided assetModel`
    )
  }
  switch (component.type) {
    // TANK COMPONENTS
    case GeckoComponentModelType.VerticalTankShell: {
      return getManufactureSpecsForVerticalTankShell({
        binBoundsToGetSpecsFor,
        tankShellInstallDate: component.installationDate,
        plates: component.platesArray,
        shellCourses: component.coursesArray,
        baseInstallDate: assetModel.data.installation_date,
        latestDateToUse,
      })
    }
    case GeckoComponentModelType.HorizontalTankShell: {
      return getManufactureSpecsForHorizontalTankShell({
        binBoundsToGetSpecsFor,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.wallNominalThickness,
      })
    }
    case GeckoComponentModelType.TankFloor: {
      // Annular rings on Floors are not yet in the RAM, so we must use a FCC-backdoor
      const centerPlateRadius = forecastComponentConfig?.center_plate_radius
      // Assume the annular ring nominal is the last nominal in the nominal_by_shell_course array
      const annularRingNominal = Object.values(
        ((forecastComponentConfig as Record<string, unknown>)
          ?.nominal_by_shell_course as Record<number, number> | undefined) ?? {}
      )?.at(-1)
      if (!!centerPlateRadius !== !!annularRingNominal) {
        console.warn(
          'Only an annular ring radius or nominal was provided, but not both. This may lead to incorrect results.'
        )
      }
      const annularRingData: AnnularRingData | undefined =
        !centerPlateRadius || !annularRingNominal
          ? undefined
          : {
              nominalThickness: GeckoMeasurement.CreateFromValue(
                annularRingNominal,
                GeckoMeasurementUnit.Inches
              ),
              start_radius: GeckoMeasurement.CreateFromValue(
                centerPlateRadius,
                GeckoMeasurementUnit.Feet
              ),
            }
      return getManufactureSpecsForTankFloor({
        binBoundsToGetSpecsFor,
        plates: component.platesArray,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.baseNominalThickness,
        floorRadius: component.radius,
        annularRingData,
        latestDateToUse,
      })
    }
    case GeckoComponentModelType.TankFlatRoof: {
      return getManufactureSpecsForTankFlatRoof({
        binBoundsToGetSpecsFor,
        plates: component.platesArray,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.baseNominalThickness,
        latestDateToUse,
      })
    }
    // BOILERS
    case GeckoComponentModelType.LegacyBoilerWalls: {
      return getManufactureSpecsForLegacyBoilerWalls({
        binBoundsToGetSpecsFor,
        legacyBoilerWallsInstallDate: component.installationDate,
        allRepairs: component.repairRegionsArray,
        baseInstallDate: assetModel.data.installation_date,
        walls: component.wallsArray,
        latestDateToUse,
      })
    }
    case GeckoComponentModelType.LegacyCone: {
      return getManufactureSpecsForCone({
        courses: component.coursesArray,
        baseInstallDate: assetModel.data.installation_date,
        legacyConeInstallDate: component.installationDate,
        binBoundsToGetSpecsFor,
      })
    }
    case GeckoComponentModelType.Cone: {
      return getManufactureSpecsForCone({
        courses: component.coursesArray,
        baseInstallDate: assetModel.data.installation_date,
        legacyConeInstallDate: component.installationDate,
        binBoundsToGetSpecsFor,
      })
    }
    // PIPES
    case GeckoComponentModelType.PipeSection: {
      return getManufactureSpecsForPipe({
        binBoundsToGetSpecsFor,
        pipeSectionInstallDate: component.installationDate,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.wallNominalThickness,
      })
    }
    // SHIPS
    case GeckoComponentModelType.ShipDeck: {
      return getManufactureSpecsForShipDeck({
        binBoundsToGetSpecsFor,
        polygonalPlates: component.polygonalPlatesArray,
        baseInstallDate: assetModel.data.installation_date,
        latestDateToUse,
      })
    }

    case GeckoComponentModelType.BasicShipHull: {
      return getManufactureSpecsForShipHull({
        binBoundsToGetSpecsFor,
        plates: component.platesArray,
        baseInstallDate: assetModel.data.installation_date,
        latestDateToUse,
      })
    }

    case GeckoComponentModelType.LaunchTube: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }

    case GeckoComponentModelType.Dome: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }

    case GeckoComponentModelType.DomeWall: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }

    case GeckoComponentModelType.FlatCircularFloor: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }
    case GeckoComponentModelType.VerticalTankFoundation: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }
    case GeckoComponentModelType.MachinedForgingSimple: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }
    case GeckoComponentModelType.PressureVesselShell: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }
    case GeckoComponentModelType.HemisphericalHead: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }
    case GeckoComponentModelType.TorisphericalHead: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }
    case GeckoComponentModelType.EllipsoidalHead: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }
  }
}
//-----------------------------------------------------------------------------

const getLatestDate = (...dates: (Date | undefined)[]): Date | undefined => {
  return dates
    .filter((date): date is Date => date !== undefined)
    .sort((a, b) => b.getTime() - a.getTime())[0]
}

// TANK COMPONENTS
//- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// SHELLS
function getManufactureSpecsForVerticalTankShell(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  tankShellInstallDate: Date | undefined
  plates: GeckoPlate[]
  shellCourses: GeckoShellCourse[]
  baseInstallDate: Date | undefined
  latestDateToUse: Date | undefined
}) {
  const {
    binBoundsToGetSpecsFor,
    tankShellInstallDate,
    plates,
    shellCourses,
    baseInstallDate,
    latestDateToUse,
  } = props
  const mostRecentPlate = getMostRecentPlateInBin(
    plates,
    binBoundsToGetSpecsFor,
    latestDateToUse
  )

  const mostRecentInstallationDate = getLatestDate(
    baseInstallDate,
    mostRecentPlate?.installationDate,
    tankShellInstallDate
  )

  if (
    !mostRecentPlate?.installationDate ||
    mostRecentPlate.installationDate === baseInstallDate
  ) {
    // Assume that any plate with no installation date has the same as the base installation date
    //    (i.e. that it was part of the original construction)
    // With that, if we're not on a repair plate get the course that the top of the bin overlaps with
    //  because that is the shell-course we believe the lowest thickness reading in a bin will be in
    const inCourse = shellCourses.findLast((course) =>
      GeckoComponentRect.Overlaps(course.geometry, binBoundsToGetSpecsFor)
    )
    return {
      nominalThickness:
        inCourse?.wallNominalThickness ?? mostRecentPlate?.wallNominalThickness,
      installationDate: mostRecentInstallationDate,
    }
  }
  return {
    nominalThickness: mostRecentPlate?.wallNominalThickness,
    installationDate: mostRecentInstallationDate,
  }
}
function getManufactureSpecsForHorizontalTankShell(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  baseInstallDate: Date | undefined
  baseNominalThickness: GeckoMeasurement | undefined
}) {
  const { baseInstallDate, baseNominalThickness } = props
  return {
    nominalThickness: baseNominalThickness,
    installationDate: baseInstallDate,
  }
}
//- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// FLOOR
type AnnularRingData = {
  nominalThickness: GeckoMeasurement
  start_radius: GeckoMeasurement
}
/**
 * Is the center of the bin in the annular ring
 */
export function isInAnnularRing(
  binBounds: GeckoComponentRect,
  floorRadius: GeckoMeasurement | undefined,
  annularRingStartRadius: GeckoMeasurement | undefined
): boolean {
  if (!floorRadius || !annularRingStartRadius) {
    return false
  }
  const centerOfBinX = binBounds.x.start.plus(binBounds.x.delta.scale(0.5))
  const centerOfBinY = binBounds.y.start.plus(binBounds.y.delta.scale(0.5))
  const centerOfBinYConsistentUnit = centerOfBinY.convertTo(centerOfBinX.unit)
  const floorRadiusConsistentUnit = floorRadius.convertTo(centerOfBinX.unit)
  // assume the origin is (0,0), so the radius is both the center x and y of the floor
  const distanceFromCenter = Math.sqrt(
    Math.pow(centerOfBinX.num - floorRadiusConsistentUnit.num, 2) +
      Math.pow(
        centerOfBinYConsistentUnit.num - floorRadiusConsistentUnit.num,
        2
      )
  )
  const annularRingStartRadiusConsistentUnit = annularRingStartRadius.convertTo(
    centerOfBinX.unit
  )
  return distanceFromCenter >= annularRingStartRadiusConsistentUnit.num
}
function getManufactureSpecsForTankFloor(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  plates: GeckoPlate[]
  baseInstallDate: Date | undefined
  baseNominalThickness: GeckoMeasurement | undefined
  floorRadius: GeckoMeasurement | undefined
  annularRingData: AnnularRingData | undefined
  latestDateToUse: Date | undefined
}) {
  const {
    binBoundsToGetSpecsFor,
    plates,
    baseInstallDate,
    baseNominalThickness,
    floorRadius,
    annularRingData,
    latestDateToUse,
  } = props
  const mostRecentPlate = getMostRecentPlateInBin(
    plates,
    binBoundsToGetSpecsFor,
    latestDateToUse
  )
  if (
    !mostRecentPlate?.installationDate ||
    mostRecentPlate.installationDate === baseInstallDate
  ) {
    // Assume that any plate with no installation date has the same as the base installation date
    //    (i.e. that it was part of the original construction)
    return {
      nominalThickness:
        (mostRecentPlate?.wallNominalThickness ??
        isInAnnularRing(
          binBoundsToGetSpecsFor,
          floorRadius,
          annularRingData?.start_radius
        ))
          ? annularRingData!.nominalThickness // we can assert this value because we know we're in the annular ring
          : baseNominalThickness,
      installationDate: baseInstallDate,
    }
  }
  return {
    nominalThickness: mostRecentPlate.wallNominalThickness,
    installationDate: mostRecentPlate.installationDate,
  }
}
//- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// ROOF
function getManufactureSpecsForTankFlatRoof(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  plates: GeckoPlate[]
  baseInstallDate: Date | undefined
  baseNominalThickness: GeckoMeasurement | undefined
  latestDateToUse: Date | undefined
}) {
  const {
    binBoundsToGetSpecsFor,
    plates,
    baseInstallDate,
    baseNominalThickness,
    latestDateToUse,
  } = props
  const mostRecentPlate = getMostRecentPlateInBin(
    plates,
    binBoundsToGetSpecsFor,
    latestDateToUse
  )
  if (
    !mostRecentPlate?.installationDate ||
    mostRecentPlate.installationDate === baseInstallDate
  ) {
    // Assume that any plate with no installation date has the same as the base installation date
    //    (i.e. that it was part of the original construction)
    return {
      nominalThickness:
        mostRecentPlate?.wallNominalThickness ?? baseNominalThickness,
      installationDate: baseInstallDate,
    }
  }
  return {
    nominalThickness: mostRecentPlate?.wallNominalThickness,
    installationDate: mostRecentPlate?.installationDate,
  }
}
//- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Cone
function getManufactureSpecsForCone(props: {
  courses: Array<GeckoConeCourse>
  baseInstallDate: Date | undefined
  legacyConeInstallDate: Date | undefined
  binBoundsToGetSpecsFor: GeckoComponentRect
}) {
  const {
    courses,
    baseInstallDate,
    legacyConeInstallDate,
    binBoundsToGetSpecsFor,
  } = props
  // Assume that any plate with no installation date has the same as the base installation date
  //    (i.e. that it was part of the original construction)
  // With that, if we're not on a repair plate get the course that the top of the bin overlaps with
  //  because that is the shell-course we believe the lowest thickness reading in a bin will be in
  const inCourse = courses.find((c) =>
    GeckoComponentRect.Contains(c.geometry, binBoundsToGetSpecsFor.end)
  )
  const mostRecentInstallationDate = getLatestDate(
    baseInstallDate,
    legacyConeInstallDate
  )
  return {
    nominalThickness: inCourse?.wallNominalThickness,
    installationDate: mostRecentInstallationDate,
  }
}
//-----------------------------------------------------------------------------
// BOILER COMPONENTS
function getManufactureSpecsForLegacyBoilerWalls(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  legacyBoilerWallsInstallDate: Date | undefined
  allRepairs: GeckoLegacyBoilerRepairRegion[]
  walls: GeckoLegacyBoilerWall[]
  baseInstallDate: Date | undefined
  latestDateToUse: Date | undefined
}) {
  const {
    binBoundsToGetSpecsFor,
    legacyBoilerWallsInstallDate,
    allRepairs,
    baseInstallDate,
    walls,
    latestDateToUse,
  } = props
  const mostRecentRepair = allRepairs
    .filter((repair) => {
      return (
        (isNil(latestDateToUse)
          ? true
          : isNil(repair.installationDate)
            ? true
            : repair.installationDate <= latestDateToUse) &&
        GeckoComponentRect.Overlaps(repair.geometry, binBoundsToGetSpecsFor)
      )
    })
    // Sort by installation date (newest), with ties broken by wall thickness (thinnest)
    .toSorted((a, b) => {
      // prioritize the thinnest plates
      if (!a.wallNominalThickness && !b.wallNominalThickness) return 0
      if (!a.wallNominalThickness) return 1
      if (!b.wallNominalThickness) return -1
      return a.wallNominalThickness.lt(b.wallNominalThickness) ? -1 : 1
    })
    .toSorted((a, b) => {
      // prioritize most recent installation
      if (!a.installationDate && !b.installationDate) return 0
      if (!a.installationDate) return 1
      if (!b.installationDate) return -1
      return a.installationDate > b.installationDate ? -1 : 1
    })
    .at(0)
  const mostRecentInstallationDate = getLatestDate(
    baseInstallDate,
    mostRecentRepair?.installationDate,
    legacyBoilerWallsInstallDate
  )

  if (
    !mostRecentRepair?.installationDate ||
    mostRecentRepair.installationDate === baseInstallDate
  ) {
    // Assume that any plate with no installation date has the same as the base installation date
    //    (i.e. that it was part of the original construction)
    const wallCoordsAreOn = walls.find((wall) =>
      GeckoComponentRect.Overlaps(wall.geometry, binBoundsToGetSpecsFor)
    )
    return {
      nominalThickness: wallCoordsAreOn?.wallNominalThickness,
      installationDate: mostRecentInstallationDate,
    }
  }
  return {
    nominalThickness: mostRecentRepair?.wallNominalThickness,
    installationDate: mostRecentInstallationDate,
  }
}
//-----------------------------------------------------------------------------
// PIPES
function getManufactureSpecsForPipe(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  pipeSectionInstallDate: Date | undefined
  baseInstallDate: Date | undefined
  baseNominalThickness: GeckoMeasurement | undefined
}) {
  const { baseInstallDate, baseNominalThickness, pipeSectionInstallDate } =
    props
  const mostRecentInstallationDate = getLatestDate(
    baseInstallDate,
    pipeSectionInstallDate
  )
  return {
    nominalThickness: baseNominalThickness,
    installationDate: mostRecentInstallationDate,
  }
}
//-----------------------------------------------------------------------------
// SHIPS
function getManufactureSpecsForShipDeck(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  polygonalPlates: GeckoPolygonalPlate[]
  baseInstallDate: Date | undefined
  latestDateToUse: Date | undefined
}) {
  const {
    binBoundsToGetSpecsFor,
    polygonalPlates,
    baseInstallDate,
    latestDateToUse,
  } = props
  const mostRecentPlate = getMostRecentPolygonalPlateInBin(
    polygonalPlates,
    binBoundsToGetSpecsFor,
    latestDateToUse
  )
  if (
    !mostRecentPlate?.installationDate ||
    mostRecentPlate.installationDate === baseInstallDate
  ) {
    // Assume that any plate with no installation date has the same as the base installation date
    //    (i.e. that it was part of the original construction)
    return {
      // TODO: implement ship-deck nominals
      nominalThickness: mostRecentPlate?.wallNominalThickness,
      installationDate: baseInstallDate,
    }
  }
  return {
    nominalThickness: mostRecentPlate?.wallNominalThickness,
    installationDate: mostRecentPlate?.installationDate,
  }
}

function getManufactureSpecsForShipHull(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  plates: GeckoPlate[]
  baseInstallDate: Date | undefined
  latestDateToUse: Date | undefined
}) {
  const { binBoundsToGetSpecsFor, plates, baseInstallDate, latestDateToUse } =
    props

  const mostRecentPlate = getMostRecentPlateInBin(
    plates,
    binBoundsToGetSpecsFor,
    latestDateToUse
  )

  if (
    !mostRecentPlate?.installationDate ||
    mostRecentPlate.installationDate === baseInstallDate
  ) {
    // Assume that any plate with no installation date has the same as the base installation date
    //    (i.e. that it was part of the original construction)

    return {
      // TODO: implement ship-deck nominals
      nominalThickness: mostRecentPlate?.wallNominalThickness,
      installationDate: baseInstallDate,
    }
  }

  return {
    nominalThickness: mostRecentPlate?.wallNominalThickness,
    installationDate: mostRecentPlate?.installationDate,
  }
}

// ----------------------------------------------------------------------------------------------------------------

/**
 * A function to get the radius from a `GeckoComponentModel`, if applicable
 */
export function getComponentRadius(
  componentRam: GeckoComponentModel | undefined
): GeckoMeasurement | undefined {
  if (!componentRam) return undefined
  switch (componentRam.type) {
    case GeckoComponentModelType.VerticalTankShell: {
      return componentRam.radius
    }
    case GeckoComponentModelType.HorizontalTankShell: {
      return componentRam.radius
    }
    case GeckoComponentModelType.TankFloor: {
      return componentRam.radius
    }
    case GeckoComponentModelType.TankFlatRoof: {
      return componentRam.radius
    }
    case GeckoComponentModelType.PipeSection: {
      return componentRam.radius
    }
    case GeckoComponentModelType.LegacyCone: {
      return componentRam.outerRadius
    }
    case GeckoComponentModelType.LaunchTube: {
      return componentRam.radius
    }
    case GeckoComponentModelType.ShipDeck: {
      return undefined
    }
    case GeckoComponentModelType.LegacyBoilerWalls: {
      return undefined
    }
  }
}
// ----------------------------------------------------------------------------------------------------------------
/**
 * A function to get the height from a `GeckoComponentModel`, if applicable
 */
export function getComponentHeight(
  componentRam: GeckoComponentModel | undefined
): GeckoMeasurement | undefined {
  if (!componentRam) return undefined
  switch (componentRam.type) {
    case GeckoComponentModelType.VerticalTankShell: {
      return componentRam.height
    }
    case GeckoComponentModelType.HorizontalTankShell: {
      return undefined
    }
    case GeckoComponentModelType.TankFloor: {
      return undefined
    }
    case GeckoComponentModelType.TankFlatRoof: {
      return undefined
    }
    case GeckoComponentModelType.PipeSection: {
      return undefined
    }
    case GeckoComponentModelType.LegacyCone: {
      return undefined
    }
    case GeckoComponentModelType.LaunchTube: {
      return componentRam.height
    }
    case GeckoComponentModelType.ShipDeck: {
      return undefined
    }
    case GeckoComponentModelType.LegacyBoilerWalls: {
      return componentRam.height
    }
  }
}
